import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FloppyDisk, GenderMale, GenderFemale, Moon, Sun, Desktop, Envelope, X, Scales, Target, Bell, BellSlash, Flame, Drop } from "@phosphor-icons/react";
import EmailLinkPanel from "@/components/EmailLinkPanel";
import DeleteAccountSection from "@/components/DeleteAccountSection";
import { useWeightUnit } from "@/lib/units";
import { PRIMARY_GOALS } from "@/lib/goals";
import {
  isPushSupported,
  pushPermission,
  getActiveSubscription,
  subscribePush,
  unsubscribePush,
  sendTestPush,
} from "@/lib/push";

const GENDER_OPTIONS = [
  { value: "male", label: "Male", icon: GenderMale },
  { value: "female", label: "Female", icon: GenderFemale },
];

export default function Settings({ api, profile, onProfileUpdate, theme, onThemeChange }) {
  const { unit: weightUnit, setUnit: setWeightUnit, toKg, toInputValue, label: weightLabel } = useWeightUnit();
  const [gender, setGender] = useState(profile?.gender || "");
  const [age, setAge] = useState(profile?.age?.toString() || "");
  const [weight, setWeight] = useState(toInputValue(profile?.weight, 1));
  const [height, setHeight] = useState(profile?.height?.toString() || "");
  const [primaryGoal, setPrimaryGoal] = useState(profile?.primary_goal || "");
  const [saving, setSaving] = useState(false);
  const [showLinker, setShowLinker] = useState(false);
  const [recapBusy, setRecapBusy] = useState(false);

  // ── Push notifications ──
  const supported = isPushSupported();
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [permission, setPermission] = useState(supported ? pushPermission() : "unsupported");

  useEffect(() => {
    if (!supported) return;
    let cancelled = false;
    (async () => {
      try {
        const sub = await getActiveSubscription();
        if (!cancelled) setPushOn(!!sub);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [supported]);

  const togglePush = async (next) => {
    setPushBusy(true);
    try {
      if (next) {
        await subscribePush(api);
        setPushOn(true);
        setPermission(pushPermission());
        toast.success("Push notifications enabled");
      } else {
        await unsubscribePush(api);
        setPushOn(false);
        toast.success("Push notifications disabled");
      }
      onProfileUpdate?.();
    } catch (e) {
      toast.error(e?.message || "Could not change notification setting");
      setPermission(pushPermission());
    } finally {
      setPushBusy(false);
    }
  };

  const sendTest = async () => {
    setPushBusy(true);
    try {
      await sendTestPush(api, { title: "UpTop", body: "Test push delivered — you're ready 💪" });
      toast.success("Test notification sent");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Could not send test";
      toast.error(typeof msg === "string" ? msg : "Could not send test");
    } finally {
      setPushBusy(false);
    }
  };

  const handleUnlinkEmail = async () => {
    try {
      await axios.post(`${api}/email/unlink`);
      toast.success("Email unlinked");
      onProfileUpdate?.();
    } catch (e) {
      toast.error("Failed to unlink email");
    }
  };

  const handleRecapToggle = async (next) => {
    setRecapBusy(true);
    try {
      await axios.put(`${api}/profile/email-prefs`, { recap_optin: next });
      toast.success(next ? "Weekly recap enabled" : "Weekly recap disabled");
      onProfileUpdate?.();
    } catch (e) {
      toast.error("Failed to update preferences");
    } finally {
      setRecapBusy(false);
    }
  };

  const [dashRecapBusy, setDashRecapBusy] = useState(false);
  const handleDashboardRecapToggle = async (next) => {
    setDashRecapBusy(true);
    try {
      await axios.put(`${api}/profile/dashboard-prefs`, { dashboard_recap_optin: next });
      toast.success(next ? "Dashboard recap card enabled" : "Dashboard recap card hidden");
      onProfileUpdate?.();
    } catch (e) {
      toast.error("Failed to update preferences");
    } finally {
      setDashRecapBusy(false);
    }
  };

  const handleSave = async () => {
    if (!gender || !age || !weight || !height) {
      toast.error("All fields are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${api}/profile`, {
        name: profile?.name || "",
        gender,
        age: parseInt(age),
        weight: parseFloat(toKg(parseFloat(weight)).toFixed(2)),
        height: parseFloat(height),
        primary_goal: primaryGoal || null,
      });
      toast.success("Profile updated!");
      onProfileUpdate();
    } catch (e) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-xl" data-testid="settings-page">
      <div>
        <h2
          className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-white"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          Settings
        </h2>
        <p className="text-sm text-[#71717A] mt-1">Edit your profile information</p>
      </div>

      {/* Appearance / Theme */}
      <div className="bg-[#161616] border border-[#27272A] p-6 space-y-4" data-testid="appearance-settings">
        <div>
          <h3
            className="text-sm font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Appearance
          </h3>
          <p className="text-xs text-[#71717A]">Choose how UpTop looks on your device. Dark is the standard theme.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <button
            onClick={() => onThemeChange?.("dark")}
            className={`border p-4 flex flex-col items-center gap-2 transition-all text-center ${
              theme === "dark"
                ? "border-[#E05A1D] bg-[#E05A1D]/10"
                : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
            }`}
            data-testid="theme-dark-btn"
          >
            <div className={`w-10 h-10 flex items-center justify-center border ${
              theme === "dark" ? "border-[#E05A1D] text-[#E05A1D]" : "border-[#27272A] text-[#A1A1AA]"
            }`} style={{ background: "#0F0F0F" }}>
              <Moon size={20} weight={theme === "dark" ? "fill" : "regular"} />
            </div>
            <div>
              <p className={`text-sm font-bold uppercase tracking-wide ${theme === "dark" ? "text-[#E05A1D]" : "text-white"}`}>
                Dark
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Standard</p>
            </div>
          </button>
          <button
            onClick={() => onThemeChange?.("light")}
            className={`border p-4 flex flex-col items-center gap-2 transition-all text-center ${
              theme === "light"
                ? "border-[#E05A1D] bg-[#E05A1D]/10"
                : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
            }`}
            data-testid="theme-light-btn"
          >
            <div className={`w-10 h-10 flex items-center justify-center border ${
              theme === "light" ? "border-[#E05A1D] text-[#E05A1D]" : "border-[#27272A] text-[#A1A1AA]"
            }`} style={{ background: "#FAFAFA" }}>
              <Sun size={20} weight={theme === "light" ? "fill" : "regular"} />
            </div>
            <div>
              <p className={`text-sm font-bold uppercase tracking-wide ${theme === "light" ? "text-[#E05A1D]" : "text-white"}`}>
                Light
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Bright</p>
            </div>
          </button>
          <button
            onClick={() => onThemeChange?.("crimson")}
            className={`border p-4 flex flex-col items-center gap-2 transition-all text-center ${
              theme === "crimson"
                ? "border-[#DC2626] bg-[#DC2626]/10"
                : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
            }`}
            data-testid="theme-crimson-btn"
          >
            <div className={`w-10 h-10 flex items-center justify-center border ${
              theme === "crimson" ? "border-[#DC2626] text-[#DC2626]" : "border-[#27272A] text-[#A1A1AA]"
            }`} style={{ background: "#0F0F0F" }}>
              <Flame size={20} weight={theme === "crimson" ? "fill" : "regular"} />
            </div>
            <div>
              <p className={`text-sm font-bold uppercase tracking-wide ${theme === "crimson" ? "text-[#DC2626]" : "text-white"}`}>
                Crimson
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Intense</p>
            </div>
          </button>
          <button
            onClick={() => onThemeChange?.("cyan")}
            className={`border p-4 flex flex-col items-center gap-2 transition-all text-center ${
              theme === "cyan"
                ? "border-[#06B6D4] bg-[#06B6D4]/10"
                : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
            }`}
            data-testid="theme-cyan-btn"
          >
            <div className={`w-10 h-10 flex items-center justify-center border ${
              theme === "cyan" ? "border-[#06B6D4] text-[#06B6D4]" : "border-[#27272A] text-[#A1A1AA]"
            }`} style={{ background: "#0F0F0F" }}>
              <Drop size={20} weight={theme === "cyan" ? "fill" : "regular"} />
            </div>
            <div>
              <p className={`text-sm font-bold uppercase tracking-wide ${theme === "cyan" ? "text-[#06B6D4]" : "text-white"}`}>
                Cyan
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Electric</p>
            </div>
          </button>
          <button
            onClick={() => onThemeChange?.("system")}
            className={`border p-4 flex flex-col items-center gap-2 transition-all text-center ${
              theme === "system"
                ? "border-[#E05A1D] bg-[#E05A1D]/10"
                : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
            }`}
            data-testid="theme-system-btn"
          >
            <div className={`w-10 h-10 flex items-center justify-center border ${
              theme === "system" ? "border-[#E05A1D] text-[#E05A1D]" : "border-[#27272A] text-[#A1A1AA]"
            }`} style={{ background: "linear-gradient(135deg, #0F0F0F 50%, #FAFAFA 50%)" }}>
              <Desktop size={20} weight={theme === "system" ? "fill" : "regular"} />
            </div>
            <div>
              <p className={`text-sm font-bold uppercase tracking-wide ${theme === "system" ? "text-[#E05A1D]" : "text-white"}`}>
                System
              </p>
              <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Auto</p>
            </div>
          </button>
        </div>
      </div>

      {/* Units */}
      <div className="bg-[#161616] border border-[#27272A] p-6 space-y-4" data-testid="units-settings">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center border border-[#27272A] text-[#E05A1D] shrink-0">
            <Scales size={20} weight="bold" />
          </div>
          <div>
            <h3
              className="text-sm font-bold tracking-[0.1em] uppercase text-[#71717A] mb-0.5"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Weight Units
            </h3>
            <p className="text-xs text-[#71717A]">Switch every kg in the app to pounds. Your data is preserved.</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => {
              setWeightUnit("kg");
              setWeight(profile?.weight?.toString() || "");
            }}
            className={`border p-4 flex flex-col items-center gap-1 transition-all text-center ${
              weightUnit === "kg"
                ? "border-[#E05A1D] bg-[#E05A1D]/10"
                : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
            }`}
            data-testid="unit-kg-btn"
          >
            <p className={`text-2xl font-black ${weightUnit === "kg" ? "text-[#E05A1D]" : "text-white"}`}
               style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              kg
            </p>
            <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Kilograms · Metric</p>
          </button>
          <button
            onClick={() => {
              setWeightUnit("lb");
              if (profile?.weight) {
                setWeight((profile.weight * 2.20462).toFixed(1));
              }
            }}
            className={`border p-4 flex flex-col items-center gap-1 transition-all text-center ${
              weightUnit === "lb"
                ? "border-[#E05A1D] bg-[#E05A1D]/10"
                : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
            }`}
            data-testid="unit-lb-btn"
          >
            <p className={`text-2xl font-black ${weightUnit === "lb" ? "text-[#E05A1D]" : "text-white"}`}
               style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              lb
            </p>
            <p className="text-[10px] uppercase tracking-wider text-[#71717A]">Pounds · Imperial</p>
          </button>
        </div>
      </div>

      {/* Email Account */}
      <div className="bg-[#161616] border border-[#27272A] p-6 space-y-4" data-testid="email-settings">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3
              className="text-sm font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Email Account
            </h3>
            <p className="text-xs text-[#71717A]">
              {profile?.email_verified
                ? "Your email is linked. We'll use it for weekly recaps when enabled."
                : "Link an email to receive weekly recaps and sync later."}
            </p>
          </div>
          {profile?.email_verified && (
            <div className="w-9 h-9 flex items-center justify-center border border-[#E05A1D]/40 text-[#E05A1D] shrink-0">
              <Envelope size={18} weight="fill" />
            </div>
          )}
        </div>

        {profile?.email_verified ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 bg-[#0F0F0F] border border-[#27272A] px-4 py-3">
              <div className="min-w-0">
                <p className="text-[10px] text-[#71717A] uppercase tracking-wider mb-0.5">Linked email</p>
                <p className="text-sm text-white font-semibold truncate" data-testid="linked-email">{profile.email}</p>
              </div>
              <button
                onClick={handleUnlinkEmail}
                className="text-[#71717A] hover:text-red-400 transition-colors p-1"
                title="Unlink"
                data-testid="unlink-email-btn"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <label className="flex items-center justify-between gap-4 cursor-pointer bg-[#0F0F0F] border border-[#27272A] px-4 py-3">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-white">Weekly Recap</p>
                <p className="text-xs text-[#71717A] mt-0.5">Get a Monday morning summary of last week's training.</p>
              </div>
              <input
                type="checkbox"
                checked={!!profile.email_recap_optin}
                onChange={(e) => handleRecapToggle(e.target.checked)}
                disabled={recapBusy}
                className="w-5 h-5 accent-[#E05A1D] cursor-pointer disabled:opacity-50"
                data-testid="recap-toggle"
              />
            </label>
          </div>
        ) : showLinker ? (
          <EmailLinkPanel
            api={api}
            compact
            onLinked={() => {
              setShowLinker(false);
              onProfileUpdate?.();
            }}
          />
        ) : (
          <button
            onClick={() => setShowLinker(true)}
            className="w-full bg-[#0F0F0F] border border-[#27272A] hover:border-[#E05A1D] text-white font-bold uppercase tracking-wide py-3 text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            data-testid="link-email-btn"
          >
            <Envelope size={16} weight="bold" />
            Link Email
          </button>
        )}
      </div>

      {/* Notifications */}
      <div className="bg-[#161616] border border-[#27272A] p-6 space-y-4" data-testid="push-settings">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center border border-[#27272A] text-[#E05A1D] shrink-0">
            {pushOn ? <Bell size={20} weight="fill" /> : <BellSlash size={20} weight="bold" />}
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="text-sm font-bold tracking-[0.1em] uppercase text-[#71717A] mb-0.5"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Notifications
            </h3>
            <p className="text-xs text-[#71717A]">
              Workout reminders, PR celebrations, and weekly recaps — straight to your phone.
            </p>
          </div>
        </div>

        {!supported ? (
          <div className="bg-[#0F0F0F] border border-[#27272A] p-3">
            <p className="text-xs text-[#A1A1AA]">
              Your browser doesn't support web push. Install UpTop to your home screen and reopen it there for full support.
            </p>
          </div>
        ) : permission === "denied" ? (
          <div className="bg-red-500/10 border border-red-500/30 p-3">
            <p className="text-xs text-red-300">
              Notifications are blocked in your browser settings. Allow them for this site, then reload.
            </p>
          </div>
        ) : (
          <>
            <label className="flex items-center justify-between gap-4 cursor-pointer bg-[#0F0F0F] border border-[#27272A] px-4 py-3">
              <div>
                <p className="text-sm font-bold uppercase tracking-wide text-white">Enable Push</p>
                <p className="text-xs text-[#71717A] mt-0.5">{pushOn ? "On for this device" : "Off"}</p>
              </div>
              <input
                type="checkbox"
                checked={pushOn}
                onChange={(e) => togglePush(e.target.checked)}
                disabled={pushBusy}
                className="w-5 h-5 accent-[#E05A1D] cursor-pointer disabled:opacity-50"
                data-testid="push-toggle"
              />
            </label>
            {pushOn && (
              <button
                onClick={sendTest}
                disabled={pushBusy}
                className="w-full bg-[#0F0F0F] border border-[#27272A] hover:border-[#E05A1D] disabled:opacity-50 text-white font-bold uppercase tracking-wide py-2.5 text-xs flex items-center justify-center gap-2 transition-all"
                data-testid="push-test-btn"
              >
                <Bell size={14} weight="bold" />
                Send a test notification
              </button>
            )}
          </>
        )}
      </div>


      <div className="bg-[#161616] border border-[#27272A] p-6 space-y-5" data-testid="profile-edit-form">
        {/* Gender */}
        <div>
          <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-2 block">Gender</label>
          <div className="grid grid-cols-3 gap-3">
            {GENDER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setGender(opt.value)}
                className={`border p-4 flex flex-col items-center gap-2 transition-all ${
                  gender === opt.value
                    ? "border-[#E05A1D] bg-[#E05A1D]/10 text-[#E05A1D]"
                    : "border-[#27272A] bg-[#0F0F0F] text-[#A1A1AA] hover:border-[#3f3f46] hover:text-white"
                }`}
                data-testid={`settings-gender-${opt.value}`}
              >
                <opt.icon size={24} weight={gender === opt.value ? "fill" : "regular"} />
                <span className="text-xs font-bold uppercase tracking-wider">{opt.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Age */}
        <div>
          <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Age</label>
          <div className="relative">
            <input
              type="number"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-4 py-3 text-sm focus:border-[#E05A1D] outline-none transition-all"
              placeholder="25"
              min="1"
              max="100"
              data-testid="settings-age-input"
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] text-xs font-bold uppercase">years</span>
          </div>
        </div>

        {/* Weight + Height */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Weight</label>
            <div className="relative">
              <input
                type="number"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-4 py-3 text-sm focus:border-[#E05A1D] outline-none transition-all"
                placeholder={weightUnit === "lb" ? "176" : "80"}
                step="0.1"
                min={weightUnit === "lb" ? "44" : "20"}
                max={weightUnit === "lb" ? "660" : "300"}
                data-testid="settings-weight-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] text-xs font-bold uppercase">{weightLabel}</span>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A] mb-1 block">Height</label>
            <div className="relative">
              <input
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                className="w-full bg-[#0F0F0F] border border-[#27272A] text-white px-4 py-3 text-sm focus:border-[#E05A1D] outline-none transition-all"
                placeholder="180"
                step="0.1"
                min="120"
                max="230"
                data-testid="settings-height-input"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717A] text-xs font-bold uppercase">cm</span>
            </div>
          </div>
        </div>

        {/* Primary Goal */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Target size={16} weight="bold" className="text-[#E05A1D]" />
            <label className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]">Primary Goal</label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {PRIMARY_GOALS.map((g) => {
              const Icon = g.icon;
              const active = primaryGoal === g.value;
              return (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setPrimaryGoal(g.value)}
                  className={`border p-3 flex items-center gap-3 transition-all text-left ${
                    active
                      ? "border-[#E05A1D] bg-[#E05A1D]/10"
                      : "border-[#27272A] bg-[#0F0F0F] hover:border-[#3f3f46]"
                  }`}
                  data-testid={`settings-goal-${g.value}`}
                >
                  <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${
                    active ? "bg-[#E05A1D]/20 text-[#E05A1D]" : "text-[#A1A1AA]"
                  }`}>
                    <Icon size={16} weight="bold" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-wide text-white truncate">{g.label}</p>
                    <p className="text-[10px] text-[#71717A] truncate">{g.headline}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Save */}
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#E05A1D] hover:bg-[#F06B2E] text-white font-bold uppercase tracking-wide px-6 py-3 text-sm flex items-center gap-2 transition-colors disabled:opacity-50 active:scale-[0.98]"
          data-testid="settings-save-btn"
        >
          <FloppyDisk size={16} weight="bold" />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {/* Current Profile Summary */}
      {profile && (
        <div className="bg-[#161616] border border-[#27272A] p-5" data-testid="profile-summary">
          <h3
            className="text-sm font-bold tracking-[0.1em] uppercase text-[#71717A] mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Current Profile
          </h3>
          <div className="grid grid-cols-4 gap-3">
            <div className="text-center">
              <p className="text-xs text-[#71717A] uppercase">Gender</p>
              <p className="text-sm font-semibold text-white capitalize">{profile.gender}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#71717A] uppercase">Age</p>
              <p className="text-sm font-semibold text-white">{profile.age}y</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#71717A] uppercase">Weight</p>
              <p className="text-sm font-semibold text-white">{toInputValue(profile.weight, 1)}{weightLabel}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#71717A] uppercase">Height</p>
              <p className="text-sm font-semibold text-white">{profile.height}cm</p>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard preferences */}
      {profile && (
        <div
          className="bg-[#161616] border border-[#27272A] p-5"
          data-testid="settings-dashboard-prefs"
        >
          <h3
            className="text-sm font-bold tracking-[0.1em] uppercase text-[#71717A] mb-3"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Dashboard
          </h3>
          <label className="flex items-center justify-between gap-4 cursor-pointer bg-[#0F0F0F] border border-[#27272A] px-4 py-3">
            <div>
              <p className="text-sm font-bold uppercase tracking-wide text-white">
                Weekly recap card
              </p>
              <p className="text-xs text-[#71717A] mt-0.5">
                Show a "your week in lifts" summary on the dashboard each week. Shareable as a story image.
              </p>
            </div>
            <input
              type="checkbox"
              checked={profile.dashboard_recap_optin !== false}
              onChange={(e) => handleDashboardRecapToggle(e.target.checked)}
              disabled={dashRecapBusy}
              className="w-5 h-5 accent-[#E05A1D] cursor-pointer disabled:opacity-50"
              data-testid="dashboard-recap-toggle"
            />
          </label>
        </div>
      )}

      {/* Legal */}
      <div
        className="bg-[#161616] border border-[#27272A] p-5 flex flex-wrap items-center gap-x-4 gap-y-2"
        data-testid="settings-legal-links"
      >
        <span
          className="text-xs font-bold tracking-[0.1em] uppercase text-[#71717A]"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        >
          Legal
        </span>
        <a
          href="/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA] hover:text-[#E05A1D] transition-colors"
          data-testid="settings-privacy-link"
        >
          Privacy Policy →
        </a>
        <a
          href="/terms"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA] hover:text-[#E05A1D] transition-colors"
          data-testid="settings-terms-link"
        >
          Terms of Service →
        </a>
      </div>

      {/* Danger Zone — account deletion */}
      <DeleteAccountSection api={api} profile={profile} />
    </div>
  );
}
